// @react
import React from 'react'
// @graphql
import { useQuery } from '@apollo/client'
import {
  CourseCertificatesQuery,
  CourseCertificatesQueryVariables,
} from 'lib/graphql/graphqlTypes'
import { GET_CERTIFICATES } from 'graphql/queries/learning.query'
// @common
import { ExternalRouteNames } from 'constants/routeNames'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { useMediaQuery } from '@material-ui/core'
// @components
import Box from '@bit/atd.web.box'
import Button from '@bit/atd.web.button'
import Table from '@bit/atd.web.table'
import colors from '@bit/atd.web.colors/dist/colors'
import Grid from '@bit/atd.web.grid'
import TextLink from '@bit/atd.web.text-link'
import QueryError from 'modules/common/components/Errors/QueryError'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
import strings from 'lib/constants/strings'

/**
 * 4.0.1.2 MyATD - ActivityHistory - Learning - Certifications
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3123%3A16072
 *
 *
 * endpoint: User/Profile
 * response: User/Certification
 * DTO:
 *
 * fields:
 * User/Certification	type
 * User/Certification	earnedDate
 * User/Certification	expiryDate
 *
 */

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  title: {
    display: 'block',
    fontWeight: 600,
  },
  subtitle: {
    color: colors.gray500,
    fontWeight: 600,
  },
  flexGrow: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  earned: {
    whiteSpace: 'nowrap',
  },
  boxWrapper: {
    '& .MuiGrid-item > div': {
      display: 'flex',
    },
    '& .MuiGrid-item > div > div': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  textTitle: {
    color: '#2E2d31',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '26px',
    marginBottom: '8px',
  },
  textDescription: {
    color: '#424140',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
  textAction: {
    marginTop: '24px',
    flexGrow: 1,
    display: 'flex',
    '& > a': {
      alignSelf: 'flex-end',
    },
  },
})

const Certifications = () => {
  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const switchLayout = useMediaQuery('(min-width: 680px)')

  const { loading: isLoading, error: isError, data } = useQuery<
    CourseCertificatesQuery,
    CourseCertificatesQueryVariables
  >(GET_CERTIFICATES, { fetchPolicy: 'cache-first' })

  /******************************************
   * FUNCTIONS
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  const contentData = data?.courseCertificates?.data || []

  /**
   *
   */
  const tableRows = () => {
    if (!contentData) {
      return []
    }

    return contentData.map((item) => [
      <span key={1}>
        <strong className={classes.title}>{item.title}</strong>
      </span>,
      <span key={2}>
        <TextLink
          variant={'primary'}
          icon={'external'}
          href={ExternalRouteNames.SSO_CERTIFICATION_PORTAL_HOME}
        >
          Certification portal{'  '}
        </TextLink>
      </span>,
    ])
  }

  const emptyResultsState = (
    <Grid
      className={classes.boxWrapper}
      alignItems="stretch"
      container
      spacing={3}
    >
      <Grid item xs={switchLayout ? 6 : 12} style={{ display: 'flex' }}>
        <Box>
          <Typography className={classes.textTitle} component={'h3'}>
            {strings.ACTIVITY_HISTORY.CERTIFICATIONS.INTERESTED}{' '}
          </Typography>
          <Typography className={classes.textDescription} component={'p'}>
            {strings.ACTIVITY_HISTORY.CERTIFICATIONS.WE_OFFER}
          </Typography>
          <div className={classes.textAction}>
            <Button href={ExternalRouteNames.ATD_CERTIFICATIONS}>
              {strings.ACTIVITY_HISTORY.CERTIFICATIONS.SEE_CERT}
            </Button>
          </div>
        </Box>
      </Grid>
      <Grid item xs={switchLayout ? 6 : 12} style={{ display: 'flex' }}>
        <Box>
          <Typography className={classes.textTitle} component={'h3'}>
            {strings.ACTIVITY_HISTORY.CERTIFICATIONS.ALREADY_WORKING}
          </Typography>
          <Typography className={classes.textDescription} component={'p'}>
            {strings.ACTIVITY_HISTORY.CERTIFICATIONS.START_APPLICATION}
          </Typography>
          <div className={classes.textAction}>
            <Button href={ExternalRouteNames.SSO_CERTIFICATION_PORTAL_HOME}>
              {strings.ACTIVITY_HISTORY.CERTIFICATIONS.VIEW_PORTAL}
            </Button>
          </div>
        </Box>
      </Grid>
    </Grid>
  )

  /**
   *
   */
  const renderContent =
    contentData.length > 0 ? (
      <>
        <Table
          header={[
            {
              label: strings.ACTIVITY_HISTORY.TABLE.TITLE,
              mobileLayout: {
                width: 'full',
                ascLabel: strings.ACTIVITY_HISTORY.TABLE.ASC_LABEL_A_Z,
                descLabel: strings.ACTIVITY_HISTORY.TABLE.DESC_LABEL_A_Z,
                showLabel: false,
              },
            },
            {
              label: strings.ACTIVITY_HISTORY.TABLE.RESOURCES,
              mobileLayout: {
                width: 'full',
                ascLabel: strings.ACTIVITY_HISTORY.TABLE.ASC_LABEL_A_Z,
                descLabel: strings.ACTIVITY_HISTORY.TABLE.DESC_LABEL_A_Z,
                showLabel: false,
              },
            },
          ]}
          rows={tableRows()}
        />
      </>
    ) : (
      emptyResultsState
    )

  /******************************************
   * RENDER
   ******************************************/
  return (
    <Box
      header={{
        title: strings.ACTIVITY_HISTORY.CERTIFICATIONS.TITLE,
      }}
    >
      <ConditionalLoader condition={!isLoading}>
        {renderContent}
      </ConditionalLoader>
    </Box>
  )
}

export default Certifications
